// ABOUTME: „Deine Woche ändern“ on an application (C1): the week step of the apply flow against the listing applied to,
// ABOUTME: saved as the application's new week; pro-core gives the account manager a task for it.
import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { AppButton } from 'components/AppButton';
import { BottomSheet } from 'components/BottomSheet';
import { Text } from 'components/Text';
import { useUpdateAppliedWeekMutation } from 'modules/applications/query';
import type { Weekday } from 'modules/availability/types';
import { getTrans } from 'modules/i18n';
import { useJobListing, useWeekFit } from '../../hooks';
import {
  changedWeekRequest,
  editedDays,
  hasEdits,
  WeekEdits
} from './weekDraft';
import { WeekStep } from './WeekStep';

const ChangeWeekSheet = ({
  applicationRefId,
  listingRefId,
  onClose
}: {
  applicationRefId: string;
  listingRefId: string;
  onClose: () => void;
}) => {
  const { listing } = useJobListing(listingRefId);
  const [edits, setEdits] = useState<WeekEdits>({});
  const [editing, setEditing] = useState<Weekday | null>(null);
  const [weeklyMinutes, setWeeklyMinutes] = useState<number | undefined>();
  const [startFrom, setStartFrom] = useState<string | undefined>();
  const [holidaysOk, setHolidaysOk] = useState(false);
  const [saveToProfile, setSaveToProfile] = useState(true);
  const profile = useWeekFit(listingRefId, {}, true);
  const days = useMemo(
    () => editedDays(profile.answer, edits),
    [profile.answer, edits]
  );
  const changed = changedWeekRequest(
    days,
    edits,
    weeklyMinutes,
    startFrom,
    startFrom ?? profile.answer?.profile.startFrom ?? listing?.startDate
  );
  const edited = useWeekFit(listingRefId, changed ?? {}, !!changed);
  const answer = changed ? edited.answer ?? profile.answer : profile.answer;
  const save = useUpdateAppliedWeekMutation(applicationRefId);

  if (!listing) return null;
  const minutes =
    weeklyMinutes ??
    profile.answer?.profile.weeklyMinutes ??
    listing.maxWeeklyHourMinutes;
  const start =
    startFrom ?? profile.answer?.profile.startFrom ?? listing.startDate;

  return (
    <Box
      data-testid="change-week"
      sx={{
        p: 3,
        display: 'grid',
        gap: 2,
        overflowY: 'auto',
        maxHeight: '88vh'
      }}
    >
      <Text
        variant="h5"
        component="h2"
        color="text.primary"
        sx={{ fontWeight: 700 }}
      >
        {getTrans('applications.detail.appliedWith.changeWeek')}
      </Text>
      <WeekStep
        listing={listing}
        answer={answer}
        profileAnswer={profile.answer}
        applied
        isError={profile.isError || edited.isError}
        isChecking={!!changed && edited.isChecking}
        edits={edits}
        editing={editing}
        onEdit={setEditing}
        onChangeDay={(day, ranges) =>
          setEdits(current => ({ ...current, [day]: ranges }))
        }
        weeklyMinutes={minutes}
        onWeeklyMinutes={setWeeklyMinutes}
        startFrom={start}
        onStartFrom={setStartFrom}
        holidaysOk={holidaysOk}
        onHolidaysOk={setHolidaysOk}
        saveToProfile={saveToProfile}
        onSaveToProfile={setSaveToProfile}
      />
      {save.isError && (
        <Alert severity="error">
          {getTrans('applications.detail.appliedWith.changeFailed')}
        </Alert>
      )}
      {!editing && (
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <AppButton
            variant="text"
            onClick={onClose}
            testId="change-week-cancel"
          >
            {getTrans('general.cancel')}
          </AppButton>
          <AppButton
            testId="change-week-save"
            // Without the profile's answer `days` holds only the edits, and saving them would empty the rest of the week.
            disabled={
              save.isLoading ||
              !profile.answer ||
              edited.isChecking ||
              edited.isError
            }
            onClick={() =>
              save.mutate(
                {
                  days,
                  weeklyMinutes: minutes,
                  startFrom: start || undefined,
                  saveToProfile: hasEdits(edits) && saveToProfile
                },
                { onSuccess: onClose }
              )
            }
          >
            {getTrans('general.save')}
          </AppButton>
        </Box>
      )}
    </Box>
  );
};

/** The link under „Damit hast du dich beworben“, and the sheet it opens. */
export const ChangeWeek = ({
  applicationRefId,
  listingRefId
}: {
  applicationRefId: string;
  listingRefId: string;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Link
        component="button"
        type="button"
        variant="body2"
        onClick={() => setOpen(true)}
        data-testid="change-week-open"
        sx={{ fontWeight: 600 }}
      >
        {getTrans('applications.detail.appliedWith.changeWeek')}
      </Link>
      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        disableBackdropClose
        ariaLabel={getTrans('applications.detail.appliedWith.changeWeek')}
      >
        {open && (
          <ChangeWeekSheet
            applicationRefId={applicationRefId}
            listingRefId={listingRefId}
            onClose={() => setOpen(false)}
          />
        )}
      </BottomSheet>
    </>
  );
};
